/**
 * One drawing's conversation (`KLQ-20260820-001` phase 1).
 *
 * **This is where somebody writes**, and it needs no canvas: `pin_id` is nullable and a loose
 * comment has always been a first-class row. That is the whole reason the conversation ships
 * before the viewer — an architect can ask the engineer a question about A-201 today, in the
 * product, instead of in an email nobody can find in March.
 *
 * **Threading is rendered one level deep and no deeper.** The server returns the thread flat
 * with `parent_comment_id` on each row, deliberately: *"the right rail renders a handful of
 * threads and nesting them server-side would fix a presentation decision in the wire
 * contract"*. Rendering unbounded nesting here would put that decision back, in the place with
 * the least room for it.
 *
 * **Resolve is offered or absent, never disabled.** The server splits the authority in two —
 * `resolve_own_comment` for the author, `resolve_any_comment` for everybody else — and a
 * client cannot evaluate the second without the caller's level, which this screen does not
 * hold. So the control is offered on the caller's own comments and on nobody else's, and the
 * server refuses independently. A disabled button that might have worked is worse than a
 * missing one.
 *
 * Mounted twice by design: here, in C1a's slot detail pane, and later in the viewer's right
 * rail. Retro rule 8 — the cross-cutting object lands once, the first time it appears.
 */

import { Fragment, useMemo, useState } from 'react';

import { Button, InlineError, Pill, SectionTitle, Select } from '@/components';
import { relativeTime } from '@/features/projects';
import { useMe } from '@/features/session';
import type { MessageKeys } from '@/lib/i18n';
import { useLocale, useMessages } from '@/lib/i18n';

import type { Audience, Comment } from './api';
import styles from './Comments.module.css';
import { feedErrorKey } from './problems';
import { useComments, useCreateComment, useResolveComment } from './queries';

/**
 * What the viewer knows about one pin, for the card that belongs to it.
 *
 * Both halves are decided **once**, on the screen that holds both the pin list and the thread,
 * and handed down — so a number on the sheet and a number on a card cannot disagree, and the
 * badge is drawn from `source_version_counter` rather than from a version fetched per pin.
 */
export interface PinAnnotation {
  /** ①②③ — the marker's number on the sheet. */
  readonly number: number;
  /** The counter of the pin's own version, when that is behind the one being viewed. */
  readonly carriedFrom: number | null;
}

/** Keyed by `pin_id`. Absent for a loose comment, which takes no number and no badge. */
export type PinAnnotations = ReadonlyMap<string, PinAnnotation>;

export interface CommentThreadProps {
  readonly projectId: string;
  readonly slotId: string;
  readonly canWrite: boolean;
  /** The row a deep link arrived on, highlighted and scrolled to. */
  readonly highlightId?: string | null;
  /**
   * The pins' numbers and carry-forward, when a canvas is beside this thread.
   *
   * Optional so that C1a's slot detail pane — which has no sheet to number against — keeps
   * rendering exactly as it did before the viewer existed.
   */
  readonly pins?: PinAnnotations | undefined;
  /** Fires when somebody presses a card's number. The viewer centres that pin. */
  readonly onSelectPin?: ((pinId: string) => void) | undefined;
  /**
   * True when the surface beside this thread is a sheet in Comment mode.
   *
   * It changes one sentence and only one: the empty state. Wireframe 2's is *"No comments yet.
   * Tap on the drawing to place a pin"*, and it is worth having because it is the only place
   * the product ever says how a pin gets made — but it is a lie in C1a's slot pane, which has
   * no drawing to tap, and in View mode, where a tap does nothing. Absent means the thread's
   * own sentence stands, which is what every caller before the viewer got.
   */
  readonly canPlacePin?: boolean | undefined;
  /**
   * Render the sort and filter controls above the list.
   *
   * The viewer's right rail passes this; C1a's slot pane does not, and its markup is
   * byte-identical to what it was before this prop existed.
   */
  readonly controls?: boolean | undefined;
}

type ThreadSort = 'newest' | 'oldest';
type ThreadFilter = 'all' | 'open' | 'resolved';

/** One option of the merged sort × filter control — the tablet rail's single box (issue #73). */
interface ThreadView {
  readonly sort: ThreadSort;
  readonly filter: ThreadFilter;
  readonly label: MessageKeys['drawings'];
}

/**
 * Every combination, sort first — the order a person scans the box in. TWIN: the two desktop
 * selects' options below; a value added to either set needs its combinations here.
 */
const THREAD_VIEWS: readonly ThreadView[] = [
  { sort: 'newest', filter: 'all', label: 'thread.mergedNewestAll' },
  { sort: 'newest', filter: 'open', label: 'thread.mergedNewestOpen' },
  { sort: 'newest', filter: 'resolved', label: 'thread.mergedNewestResolved' },
  { sort: 'oldest', filter: 'all', label: 'thread.mergedOldestAll' },
  { sort: 'oldest', filter: 'open', label: 'thread.mergedOldestOpen' },
  { sort: 'oldest', filter: 'resolved', label: 'thread.mergedOldestResolved' },
];

function viewKey(sort: ThreadSort, filter: ThreadFilter): string {
  return `${sort}:${filter}`;
}

export function CommentThread({
  projectId,
  slotId,
  canWrite,
  highlightId = null,
  pins,
  onSelectPin,
  canPlacePin = false,
  controls = false,
}: CommentThreadProps) {
  const t = useMessages('drawings');
  const { locale } = useLocale();
  const me = useMe();
  const thread = useComments(projectId, slotId);
  const create = useCreateComment(projectId);
  const resolve = useResolveComment(projectId);

  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  // The default is the safety property, so it is **not** sticky: every send puts it back to
  // `internal` (`MCK-20260814-001`). A control that remembered `project` would make the next
  // comment outward because of a decision taken about the last one.
  const [audience, setAudience] = useState<Audience>('internal');
  const [sort, setSort] = useState<ThreadSort>('newest');
  const [filter, setFilter] = useState<ThreadFilter>('all');

  const rows = thread.data?.items ?? [];
  const allRoots = rows.filter((row) => row.parent_comment_id == null);
  const roots = useMemo(() => {
    if (!controls) return allRoots;
    const filtered =
      filter === 'all'
        ? allRoots
        : allRoots.filter((row) =>
            filter === 'open' ? row.resolved_at == null : row.resolved_at != null,
          );
    return [...filtered].sort((a, b) =>
      sort === 'newest'
        ? b.created_at.localeCompare(a.created_at)
        : a.created_at.localeCompare(b.created_at),
    );
  }, [allRoots, controls, sort, filter]);
  const repliesOf = (parentId: string): readonly Comment[] =>
    rows.filter((row) => row.parent_comment_id === parentId);

  const submit = (): void => {
    const body = draft.trim();
    if (body === '') return;
    create.mutate(
      {
        slot_id: slotId,
        body,
        // A reply names no audience at all: it inherits its parent's, and naming a different
        // one is a `409`. Sending the composer's value here would refuse every reply to a
        // `project` comment.
        ...(replyTo === null ? { audience } : { parent_comment_id: replyTo }),
      },
      {
        onSuccess: () => {
          setDraft('');
          setReplyTo(null);
          setAudience('internal');
        },
      },
    );
  };

  const card = (row: Comment, isReply: boolean) => {
    // A row whose author has been erased (`author_user_id` is null after an erasure) has no
    // owner, so nobody inherits `own` — the server takes it to `resolve_any_comment` and this
    // screen offers nothing.
    const mine = row.author_user_id != null && row.author_user_id === me.data?.user_id;
    // The topic is numbered, not each thing said about it: a reply carries its parent's
    // `pin_id`, and numbering it again would put ① on the sheet and ① twice in the rail.
    const pin = isReply || row.pin_id == null ? undefined : pins?.get(row.pin_id);
    return (
      <li
        key={row.id}
        className={isReply ? styles.reply : undefined}
        id={`comment-${row.id}`}
        ref={
          row.id === highlightId
            ? (node) => {
                node?.scrollIntoView({ block: 'nearest' });
              }
            : undefined
        }
      >
        <div
          className={[styles.entry, row.id === highlightId ? styles.highlighted : '']
            .filter(Boolean)
            .join(' ')}
          // Not a button: a thread card is read in place, so making it activatable would
          // promise a destination that does not exist. The feed's rows are buttons because
          // theirs does.
        >
          <span className={styles.entryHead}>
            {/* The number and the "Pinned" chip are the same fact told twice, so only one is
                shown: beside a canvas the number is strictly more useful, because it names
                *which* pin and can be pressed to fly to it. The chip is what is left whenever
                there is no number — C1a's slot pane, where `pins` is not supplied, and equally
                a pin the viewer cannot place, which takes no number precisely so that no card
                offers to fly to a marker that was never drawn. A reply gets neither: its
                parent already carries both. */}
            {pin === undefined ? (
              row.pin_id != null ? (
                <Pill tone="warning">{t('feed.badge.pinned')}</Pill>
              ) : null
            ) : (
              <button
                type="button"
                className={styles.pinNumber}
                aria-label={t('thread.centrePin', { number: pin.number })}
                onClick={() => {
                  if (row.pin_id != null) onSelectPin?.(row.pin_id);
                }}
              >
                {pin.number}
              </button>
            )}
            {pin?.carriedFrom == null ? null : (
              <Pill tone="rev">
                {t('thread.carriedFrom', { version: String(pin.carriedFrom).padStart(2, '0') })}
              </Pill>
            )}
            {/* Always rendered, for both values. A badge that appeared only on `project`
                comments would make "no badge" mean two things — internal, or a row from
                before the column existed — and the one it must never mean is *"safe to
                forward"*. */}
            <Pill tone={row.audience === 'project' ? 'info' : 'neutral'}>
              {t(row.audience === 'project' ? 'thread.audience.project' : 'thread.audience.internal')}
            </Pill>
            {row.resolved_at != null ? (
              <Pill tone="success">{t('feed.badge.resolved')}</Pill>
            ) : null}
            <span className={styles.when}>{relativeTime(row.created_at, locale)}</span>
          </span>
          <p
            className={[styles.body, row.resolved_at != null ? styles.resolvedBody : '']
              .filter(Boolean)
              .join(' ')}
          >
            {row.body}
          </p>
          {canWrite ? (
            <span className={styles.actions}>
              {isReply ? null : (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    setReplyTo(row.id);
                  }}
                >
                  {t('thread.reply')}
                </Button>
              )}
              {mine && row.resolved_at == null ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={resolve.isPending}
                  onClick={() => {
                    resolve.mutate(row.id);
                  }}
                >
                  {t('thread.resolve')}
                </Button>
              ) : null}
            </span>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <section className={styles.thread}>
      <header className={styles.head}>
        <SectionTitle as="h3">{t('thread.title')}</SectionTitle>
      </header>

      {controls ? (
        <div className={styles.controlsWide}>
          <Select
            label={t('thread.sort')}
            size="sm"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as 'newest' | 'oldest');
            }}
          >
            <option value="newest">{t('thread.sortNewest')}</option>
            <option value="oldest">{t('thread.sortOldest')}</option>
          </Select>
          <Select
            label={t('thread.filter')}
            size="sm"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as 'all' | 'open' | 'resolved');
            }}
          >
            <option value="all">{t('thread.filterAll')}</option>
            <option value="open">{t('thread.filterOpen')}</option>
            <option value="resolved">{t('thread.filterResolved')}</option>
          </Select>
        </div>
      ) : null}
      {controls ? (
        // The same two pieces of state, as one box: at 300px two selects do not fit side by
        // side, six options in one do. Choosing one sets both.
        <div className={styles.controlsMerged}>
          <Select
            label={t('thread.merged')}
            size="sm"
            value={viewKey(sort, filter)}
            onChange={(event) => {
              const view = THREAD_VIEWS.find(
                (candidate) => viewKey(candidate.sort, candidate.filter) === event.target.value,
              );
              if (view === undefined) return;
              setSort(view.sort);
              setFilter(view.filter);
            }}
          >
            {THREAD_VIEWS.map((view) => (
              <option key={view.label} value={viewKey(view.sort, view.filter)}>
                {t(view.label)}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {thread.error != null ? (
        <InlineError variant="block">{t(feedErrorKey(thread.error))}</InlineError>
      ) : thread.isPending ? (
        <p className={styles.muted}>{t('thread.loading')}</p>
      ) : rows.length === 0 ? (
        <p className={styles.muted}>
          {!canWrite
            ? t('thread.emptyReadOnly')
            : canPlacePin
              ? t('thread.emptySheet')
              : t('thread.empty')}
        </p>
      ) : (
        <ul className={styles.list}>
          {roots.map((row) => (
            // A keyed `Fragment` rather than a bare `<>`: a root and its replies are siblings
            // in one list, so the pair needs an identity of its own or React re-creates both
            // whenever anything above them changes.
            <Fragment key={row.id}>
              {card(row, false)}
              {repliesOf(row.id).map((reply) => card(reply, true))}
            </Fragment>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className={styles.composer}>
          {replyTo === null ? null : (
            <p className={styles.muted}>
              {t('thread.replyingTo')}{' '}
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setReplyTo(null);
                }}
              >
                {t('thread.cancelReply')}
              </Button>
            </p>
          )}
          {/* Above the textarea, not beside the Send button: the audience is a property of
              what is being written, and a person who discovers it at the moment of sending
              has already written the comment believing something else. Absent while replying,
              because a reply inherits its parent's audience and a control that could only
              produce a `409` is worse than no control (this file's own rule: offered or
              absent, never disabled). */}
          {replyTo === null ? (
            <div className={styles.audiencePicker}>
              <Select
                label={t('thread.audience.label')}
                size="sm"
                value={audience}
                onChange={(event) => {
                  setAudience(event.currentTarget.value as Audience);
                }}
              >
                <option value="internal">{t('thread.audience.internal')}</option>
                <option value="project">{t('thread.audience.project')}</option>
              </Select>
            </div>
          ) : null}
          <textarea
            className={styles.field}
            value={draft}
            placeholder={t('thread.placeholder')}
            aria-label={t('thread.placeholder')}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
            }}
          />
          {create.error != null ? (
            <InlineError variant="inline">{t(feedErrorKey(create.error))}</InlineError>
          ) : null}
          <span className={styles.actions}>
            <Button
              variant="primary"
              size="sm"
              disabled={draft.trim() === '' || create.isPending}
              onClick={submit}
            >
              {t('thread.send')}
            </Button>
          </span>
        </div>
      ) : null}
    </section>
  );
}
